/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/voicemailSettingsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Centralized API functions for Voicemail Settings.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  VoicemailSettingsResponse,
  VoicemailSettingsPayload,
} from '../types';

/**
 * Fetch voicemail settings
 */
export async function fetchVoicemailSettings(): Promise<VoicemailSettingsResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/voicemail-settings'), {
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch voicemail settings');
  }

  return response.json();
}

/**
 * Create voicemail settings
 */
export async function createVoicemailSettings(
  payload: VoicemailSettingsPayload
): Promise<VoicemailSettingsResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/voicemail-settings'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create voicemail settings');
  }

  return response.json();
}

/**
 * Update voicemail settings
 */
export async function updateVoicemailSettings(
  payload: VoicemailSettingsPayload
): Promise<VoicemailSettingsResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/voicemail-settings'), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update voicemail settings');
  }

  return response.json();
}

